/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2024, Zumper
 */

import { Box, Button, List, ListItem, useTheme } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import type { CarouselInternalState } from 'react-multi-carousel'

export interface ProgressDotsProps extends Partial<CarouselInternalState> {
  bottom?: string
  isMobile?: boolean
  goToSlide: (index: number) => void
  totalItems: number
  currentSlide: number
  maxDots?: number
}

export const ProgressDots = ({
  bottom = '-24px',
  totalItems,
  isMobile,
  maxDots,
  goToSlide,
  currentSlide,
}: ProgressDotsProps) => {
  const { colors } = useTheme()
  const indexOffset = useMemo(
    () => (maxDots && totalItems > maxDots ? Math.floor(currentSlide / maxDots) * maxDots : 0),
    [maxDots, totalItems, currentSlide]
  )

  const offsetExists = indexOffset !== undefined
  const maxIndex = maxDots ?? totalItems

  const indexArray = useMemo(() => Array.from(Array(maxIndex).keys()), [maxIndex])
  return (
    <Box<'div'> bottom={bottom} left="50%" position="absolute" transform="translateX(-50%)">
      <List
        zIndex={1}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent={offsetExists ? 'flex-start' : 'center'}
        margin={0}
        padding={0}
        listStyleType="none"
        gap={1}
      >
        {indexArray.map((index) => {
          const activeDot = offsetExists
            ? indexOffset + index === currentSlide
            : currentSlide === index

          const calcIndex = indexOffset && currentSlide >= maxIndex ? index + indexOffset : index

          if (calcIndex >= totalItems) {
            return null
          }

          return (
            <ListItem
              alignItems="center"
              display="flex"
              justifyContent="center"
              key={`dot-${calcIndex}`}
            >
              <Button
                _disabled={{ opacity: 1 }}
                aria-label={`slide ${String(calcIndex + 1)} of ${String(totalItems)}`}
                backgroundColor={activeDot ? colors.pink['600'] : colors.gray['150']}
                width="6px"
                minWidth="6px"
                height="6px"
                borderRadius="100%"
                padding="0px"
                border="0px"
                cursor="pointer"
                disabled={isMobile}
                onClick={(e) => {
                  e.stopPropagation()
                  goToSlide(calcIndex)
                }}
              >
                {activeDot && (
                  <Box<'span'>
                    as="span"
                    position="absolute"
                    width="1px"
                    height="1px"
                    margin="-1px"
                    padding="0"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    border="0"
                  >
                    Current Item
                  </Box>
                )}
                <Box<'span'>
                  as="span"
                  position="absolute"
                  width="1px"
                  height="1px"
                  margin="-1px"
                  padding="0"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  border="0"
                >
                  {calcIndex + 1}
                </Box>
              </Button>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}
